import React from 'react';
import { RadioProps, Label, Box } from '@apia/theme';
import useField from '../hooks/useField';
import { TDefaultFieldType } from './types';
import useValidationClass from './useValidationClass';
import Wrapper from './Wrapper';
import useFieldStatesClassNames from '../hooks/useFieldStyles';
import OuterRadio from '../../fields/Radio';

export type TRadio = RadioProps &
  TDefaultFieldType<string> & {
    options: { value: string; label: string }[];
  };

const Radio = React.forwardRef<HTMLInputElement, TRadio>(
  (
    {
      avoidSemicolon,
      className,
      formName,
      label,
      onChange: outerOnChange,
      options,
      value: outerValue,
      /**
       * useField
       */
      avoidRegisterIfExists,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });
    const { value, updateValidations, onChange, isTouched, ...inputProps } =
      useField<string>({
        formName,
        name,
        initialValue: initialValue ?? (outerValue as string),
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });
    const handleOnChange = React.useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        onChange(ev);
        if (outerOnChange) {
          outerOnChange(ev);
        }
      },
      [onChange, outerOnChange],
    );
    const stateClassName = useFieldStatesClassNames(name);
    return (
      <Wrapper
        name={name}
        label={label}
        className={`radio ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
        as="ul"
      >
        {options?.map((option) => (
          <Box key={option.value}>
            <Label
              title={option.label}
              className={`radio-label ${stateClassName}`}
            >
              <OuterRadio
                {...props}
                {...inputProps}
                role="radio"
                className="radio-button"
                aria-label={option.label}
                ref={ref}
                value={option.value}
                checked={value === option.value}
                onChange={handleOnChange}
              />
              <Box as="span" className="radio-text">
                {option.label}
              </Box>
            </Label>
          </Box>
        ))}
      </Wrapper>
    );
  },
);

export default React.memo(Radio);
